import { useState, useRef, useEffect, useMemo } from "react";

// Categorized emoji dataset ([emoji, search-name]) powering the Skool-style
// picker (search + category tabs + section labels). Dependency-free.
const CATEGORIES = [
  {
    key: "smileys",
    label: "Smileys & People",
    icon: "😀",
    list: [
      ["😀", "grinning"], ["😃", "smiley"], ["😄", "smile"], ["😁", "grin"], ["😆", "laughing"],
      ["😅", "sweat smile"], ["🤣", "rofl"], ["😂", "joy laugh"], ["🙂", "slight smile"], ["🙃", "upside down"],
      ["😉", "wink"], ["😊", "blush"], ["😇", "innocent angel"], ["🥰", "love hearts"], ["😍", "heart eyes"],
      ["🤩", "star struck"], ["😘", "kiss heart"], ["😗", "kissing"], ["😚", "kiss closed"], ["😙", "kiss smile"],
      ["😋", "yum tasty"], ["😛", "tongue"], ["😜", "wink tongue"], ["🤪", "zany goofy"], ["😝", "squint tongue"],
      ["🤑", "money mouth"], ["🤗", "hug"], ["🤭", "hand over mouth"], ["🤫", "shush quiet"], ["🤔", "thinking"],
      ["🤐", "zipper quiet"], ["😐", "neutral"], ["😑", "expressionless"], ["😶", "no mouth"], ["😏", "smirk"],
      ["😒", "unamused"], ["🙄", "roll eyes"], ["😬", "grimace"], ["😮‍💨", "exhale"], ["😴", "sleeping"],
      ["😌", "relieved"], ["😔", "pensive"], ["😪", "sleepy"], ["🤤", "drooling"], ["😷", "mask sick"],
      ["🤒", "thermometer sick"], ["🤕", "bandage hurt"], ["🤢", "nauseated"], ["🤮", "vomiting"], ["🥵", "hot"],
      ["🥶", "cold"], ["😵", "dizzy"], ["🤯", "mind blown"], ["🤠", "cowboy"], ["🥳", "party"],
      ["😎", "sunglasses cool"], ["🤓", "nerd"], ["🧐", "monocle"], ["😕", "confused"], ["😟", "worried"],
      ["🙁", "frown"], ["😮", "open mouth"], ["😯", "hushed"], ["😲", "astonished"], ["😳", "flushed"],
      ["🥺", "pleading"], ["😢", "cry"], ["😭", "sob"], ["😦", "anguished"], ["😨", "fearful"],
      ["😰", "anxious"], ["😥", "sad relieved"], ["😱", "scream"], ["😖", "confounded"], ["😣", "persevere"],
      ["😞", "disappointed"], ["😓", "sweat"], ["😩", "weary"], ["😫", "tired"], ["🥱", "yawn"],
      ["😤", "triumph"], ["😡", "rage angry"], ["😠", "angry"], ["🤬", "cursing"], ["😈", "devil"],
      ["💀", "skull"], ["💩", "poop"], ["🤡", "clown"], ["👻", "ghost"], ["👽", "alien"],
      ["🤖", "robot"], ["👋", "wave hi"], ["🤚", "raised hand"], ["✋", "hand stop"], ["👌", "ok"],
      ["🤏", "pinch"], ["✌️", "victory peace"], ["🤞", "fingers crossed"], ["🤟", "love you"], ["🤘", "rock"],
      ["👍", "thumbs up like"], ["👎", "thumbs down"], ["👊", "fist punch"], ["👏", "clap"], ["🙌", "raise hands"],
      ["🙏", "pray thanks"], ["💪", "muscle strong"], ["🫶", "heart hands"],
    ],
  },
  {
    key: "animals",
    label: "Animals & Nature",
    icon: "🐻",
    list: [
      ["🐶", "dog"], ["🐱", "cat"], ["🐭", "mouse"], ["🐹", "hamster"], ["🐰", "rabbit"],
      ["🦊", "fox"], ["🐻", "bear"], ["🐼", "panda"], ["🐨", "koala"], ["🐯", "tiger"],
      ["🦁", "lion"], ["🐮", "cow"], ["🐷", "pig"], ["🐸", "frog"], ["🐵", "monkey"],
      ["🐔", "chicken"], ["🐧", "penguin"], ["🐦", "bird"], ["🦆", "duck"], ["🦉", "owl"],
      ["🐴", "horse"], ["🦄", "unicorn"], ["🐝", "bee"], ["🐛", "bug"], ["🦋", "butterfly"],
      ["🐢", "turtle"], ["🐍", "snake"], ["🐙", "octopus"], ["🐳", "whale"], ["🐬", "dolphin"],
      ["🐟", "fish"], ["🐊", "crocodile"], ["🐘", "elephant"], ["🦒", "giraffe"], ["🐊", "croc"],
      ["🌳", "tree"], ["🌲", "evergreen"], ["🌵", "cactus"], ["🌸", "blossom"], ["🌺", "hibiscus"],
      ["🌻", "sunflower"], ["🌹", "rose"], ["🌷", "tulip"], ["🍀", "clover luck"], ["🍁", "maple leaf"],
      ["⭐", "star"], ["🌟", "glowing star"], ["🌈", "rainbow"], ["🔥", "fire"], ["💧", "water drop"],
    ],
  },
  {
    key: "food",
    label: "Food & Drink",
    icon: "🍔",
    list: [
      ["🍏", "green apple"], ["🍎", "apple"], ["🍐", "pear"], ["🍊", "orange"], ["🍋", "lemon"],
      ["🍌", "banana"], ["🍉", "watermelon"], ["🍇", "grapes"], ["🍓", "strawberry"], ["🫐", "blueberry"],
      ["🍒", "cherry"], ["🍑", "peach"], ["🥭", "mango"], ["🍍", "pineapple"], ["🥥", "coconut"],
      ["🥝", "kiwi"], ["🍅", "tomato"], ["🥑", "avocado"], ["🌽", "corn"], ["🥕", "carrot"],
      ["🍔", "burger"], ["🍟", "fries"], ["🍕", "pizza"], ["🌭", "hotdog"], ["🥪", "sandwich"],
      ["🌮", "taco"], ["🌯", "burrito"], ["🍜", "noodles ramen"], ["🍝", "pasta"], ["🍣", "sushi"],
      ["🍱", "bento"], ["🍚", "rice"], ["🍦", "ice cream"], ["🍩", "donut"], ["🍪", "cookie"],
      ["🎂", "cake birthday"], ["🍰", "cake"], ["🍫", "chocolate"], ["🍬", "candy"], ["🍿", "popcorn"],
      ["☕", "coffee"], ["🍵", "tea"], ["🥤", "soda drink"], ["🍺", "beer"], ["🍷", "wine"],
      ["🥂", "cheers"], ["🍸", "cocktail"], ["🧃", "juice"], ["🧋", "boba tea"], ["🍾", "champagne"],
    ],
  },
  {
    key: "activity",
    label: "Activities",
    icon: "⚽",
    list: [
      ["⚽", "soccer football"], ["🏀", "basketball"], ["🏈", "american football"], ["⚾", "baseball"], ["🎾", "tennis"],
      ["🏐", "volleyball"], ["🏉", "rugby"], ["🎱", "8ball pool"], ["🏓", "ping pong"], ["🏸", "badminton"],
      ["🥅", "goal"], ["🏒", "hockey"], ["🏏", "cricket"], ["⛳", "golf"], ["🏹", "archery"],
      ["🎣", "fishing"], ["🥊", "boxing"], ["🥋", "martial arts"], ["⛸️", "skating"], ["🛹", "skateboard"],
      ["🎿", "ski"], ["🏂", "snowboard"], ["🏆", "trophy"], ["🥇", "gold medal"], ["🥈", "silver medal"],
      ["🥉", "bronze medal"], ["🎖️", "medal"], ["🏅", "sports medal"], ["🎮", "game controller"], ["🎲", "dice"],
      ["🎯", "target dart"], ["🎳", "bowling"], ["🎤", "mic sing"], ["🎧", "headphones"], ["🎸", "guitar"],
      ["🎹", "piano"], ["🎺", "trumpet"], ["🥁", "drum"], ["🎨", "art paint"], ["🎬", "movie clapper"],
    ],
  },
  {
    key: "travel",
    label: "Travel & Places",
    icon: "✈️",
    list: [
      ["🚗", "car"], ["🚕", "taxi"], ["🚙", "suv"], ["🚌", "bus"], ["🏎️", "race car"],
      ["🚓", "police car"], ["🚑", "ambulance"], ["🚒", "fire truck"], ["🚜", "tractor"], ["🛵", "scooter"],
      ["🏍️", "motorcycle"], ["🚲", "bicycle"], ["✈️", "airplane"], ["🚀", "rocket"], ["🛸", "ufo"],
      ["🚁", "helicopter"], ["⛵", "sailboat"], ["🚤", "speedboat"], ["🚢", "ship"], ["🚂", "train"],
      ["🚆", "metro"], ["🗺️", "map"], ["🗽", "statue liberty"], ["🗼", "tower"], ["🏰", "castle"],
      ["🏟️", "stadium"], ["🏖️", "beach"], ["🏝️", "island"], ["⛰️", "mountain"], ["🌋", "volcano"],
      ["🏕️", "camping"], ["🏙️", "city"], ["🌃", "night city"], ["🌉", "bridge"], ["🎡", "ferris wheel"],
      ["🎢", "roller coaster"], ["🎆", "fireworks"], ["🌅", "sunrise"], ["🌄", "sunrise mountain"], ["🗿", "moai"],
    ],
  },
  {
    key: "objects",
    label: "Objects",
    icon: "💡",
    list: [
      ["⌚", "watch"], ["📱", "phone"], ["💻", "laptop"], ["🖥️", "desktop computer"], ["🖨️", "printer"],
      ["⌨️", "keyboard"], ["🖱️", "mouse"], ["💾", "floppy save"], ["💿", "cd"], ["📷", "camera"],
      ["📸", "camera flash"], ["🎥", "video camera"], ["📺", "tv"], ["📻", "radio"], ["☎️", "telephone"],
      ["🔋", "battery"], ["🔌", "plug"], ["💡", "bulb idea"], ["🔦", "flashlight"], ["🕯️", "candle"],
      ["📕", "book"], ["📚", "books"], ["📝", "memo note"], ["✏️", "pencil"], ["🖊️", "pen"],
      ["📌", "pin"], ["📎", "paperclip"], ["✂️", "scissors"], ["🔑", "key"], ["🔒", "lock"],
      ["🔓", "unlock"], ["🔔", "bell"], ["📣", "megaphone"], ["💰", "money bag"], ["💳", "credit card"],
      ["💎", "gem diamond"], ["⚙️", "gear"], ["🔧", "wrench"], ["🧰", "toolbox"], ["🛒", "cart"],
    ],
  },
  {
    key: "symbols",
    label: "Symbols",
    icon: "❤️",
    list: [
      ["❤️", "red heart love"], ["🧡", "orange heart"], ["💛", "yellow heart"], ["💚", "green heart"], ["💙", "blue heart"],
      ["💜", "purple heart"], ["🖤", "black heart"], ["🤍", "white heart"], ["🤎", "brown heart"], ["💔", "broken heart"],
      ["❣️", "heart exclamation"], ["💕", "two hearts"], ["💞", "revolving hearts"], ["💓", "beating heart"], ["💗", "growing heart"],
      ["💖", "sparkling heart"], ["💘", "heart arrow"], ["💝", "heart gift"], ["💟", "heart decoration"], ["💯", "hundred"],
      ["✨", "sparkles"], ["⭐", "star"], ["🌟", "glowing star"], ["💫", "dizzy star"], ["⚡", "zap lightning"],
      ["🔥", "fire lit"], ["💥", "boom"], ["✅", "check"], ["❌", "cross x"], ["❗", "exclamation"],
      ["❓", "question"], ["⚠️", "warning"], ["🚫", "no prohibited"], ["♻️", "recycle"], ["🔆", "bright"],
      ["🎵", "music note"], ["🎶", "notes"], ["➕", "plus"], ["➖", "minus"], ["✔️", "check mark"],
    ],
  },
  {
    key: "flags",
    label: "Flags",
    icon: "🏁",
    list: [
      ["🏁", "checkered flag"], ["🚩", "triangular flag"], ["🏳️", "white flag"], ["🏴", "black flag"], ["🏳️‍🌈", "rainbow pride flag"],
      ["🇺🇸", "usa flag"], ["🇬🇧", "uk flag"], ["🇨🇦", "canada flag"], ["🇮🇳", "india flag"], ["🇦🇺", "australia flag"],
      ["🇩🇪", "germany flag"], ["🇫🇷", "france flag"], ["🇯🇵", "japan flag"], ["🇧🇷", "brazil flag"], ["🇿🇦", "south africa flag"],
      ["🇸🇬", "singapore flag"],
    ],
  },
];

const RECENT_KEY = "emoji_recent_v1";
const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
};

function EmojiBtn({ e, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-2xl leading-none rounded-lg p-1 hover:bg-gray-100 transition"
    >
      {e}
    </button>
  );
}

// Skool-style emoji picker: search bar, category tabs (scroll anchors), section
// labels, recently-used row, and a scrollable grid. Calls onPick(emoji).
export default function EmojiPicker({ onPick, className = "", dropUp = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState(readRecent);
  const rootRef = useRef(null);
  const bodyRef = useRef(null);
  const secRefs = useRef({});

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (e) => {
    onPick(e);
    setRecent((prev) => {
      const next = [e, ...prev.filter((x) => x !== e)].slice(0, 21);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const query = q.trim().toLowerCase();
  const results = useMemo(() => {
    if (!query) return null;
    const out = [];
    for (const c of CATEGORIES)
      for (const [e, n] of c.list) if (n.includes(query)) out.push(e);
    return out;
  }, [query]);

  const scrollTo = (key) => {
    const el = secRefs.current[key];
    if (el && bodyRef.current) bodyRef.current.scrollTo({ top: el.offsetTop, behavior: "smooth" });
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Add emoji"
        className="flex items-center justify-center text-gray-500 hover:text-gray-700 transition"
      >
        <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <circle cx="12" cy="12" r="9" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 10h.01M15 10h.01M8.5 14.5a4 4 0 007 0" />
        </svg>
      </button>

      {open && (
        <div className={`fixed inset-x-3 bottom-3 z-50 w-auto sm:absolute sm:z-40 sm:inset-x-auto sm:right-0 sm:w-[332px] ${dropUp ? "sm:top-auto sm:bottom-full sm:mb-2" : "sm:bottom-auto sm:top-full sm:mt-2"} rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden`}>
          {/* Search */}
          <div className="p-2.5 border-b border-gray-100">
            <div className="relative">
              <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3M11 18a7 7 0 100-14 7 7 0 000 14z" />
              </svg>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
                className="w-full text-sm bg-gray-100 rounded-lg pl-9 pr-3 py-2 outline-none focus:bg-gray-50 placeholder:text-gray-400"
              />
            </div>
          </div>

          {/* Category tabs (hidden while searching) */}
          {!query && (
            <div className="flex items-center px-1.5 py-1 border-b border-gray-100">
              {recent.length > 0 && (
                <button
                  type="button"
                  title="Recently used"
                  onClick={() => scrollTo("recent")}
                  className="flex-1 flex items-center justify-center rounded-md py-1.5 text-gray-400 hover:bg-gray-100"
                >
                  <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              )}
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  title={c.label}
                  onClick={() => scrollTo(c.key)}
                  className="flex-1 text-lg leading-none rounded-md py-1 hover:bg-gray-100"
                >
                  {c.icon}
                </button>
              ))}
            </div>
          )}

          {/* Body */}
          <div ref={bodyRef} className="max-h-64 overflow-y-auto p-2 scrollbar-hide">
            {query ? (
              results.length > 0 ? (
                <>
                  <p className="px-1 py-1 text-xs font-semibold text-gray-400">Search Results</p>
                  <div className="grid grid-cols-7 gap-0.5">
                    {results.map((e, i) => (
                      <EmojiBtn key={i} e={e} onClick={() => pick(e)} />
                    ))}
                  </div>
                </>
              ) : (
                <p className="py-10 text-center text-sm text-gray-400">No emoji found</p>
              )
            ) : (
              <>
                {recent.length > 0 && (
                  <div ref={(el) => (secRefs.current.recent = el)}>
                    <p className="px-1 py-1 text-xs font-semibold text-gray-400">Recently used</p>
                    <div className="grid grid-cols-7 gap-0.5">
                      {recent.map((e, i) => (
                        <EmojiBtn key={i} e={e} onClick={() => pick(e)} />
                      ))}
                    </div>
                  </div>
                )}
                {CATEGORIES.map((c) => (
                  <div key={c.key} ref={(el) => (secRefs.current[c.key] = el)}>
                    <p className="px-1 py-1 mt-1 text-xs font-semibold text-gray-400">{c.label}</p>
                    <div className="grid grid-cols-7 gap-0.5">
                      {c.list.map(([e], i) => (
                        <EmojiBtn key={i} e={e} onClick={() => pick(e)} />
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
